/**
 * useMessagingPoll — Hook robusto per polling Sarabi Messaging (M2.2.2).
 *
 * Garantisce refresh visibili in produzione anche con:
 *  - browser cache / bfcache / Chrome background throttling
 *  - errori transienti (continua a pollare al ciclo successivo)
 *  - tab hidden (sospende timer, ma refresh immediato su recovery)
 *  - richieste concorrenti duplicate (in-flight guard)
 *
 * Cross-device recovery (Fase A/B):
 *   - Sottoscritto a `lifecycle.on("recovery")` → refetch + reschedule
 *     immediati al ritorno da sleep/wake, foreground, bfcache, online.
 *   - Rimossi listener grezzi visibilitychange/focus → delegati a lifecycle.
 *
 * API:
 *   const { data, error, loading, refresh } = useMessagingPoll(
 *     '/staff/messaging/whatsapp/conversations',
 *     { intervalMs: 8000, params: { limit: 100 } });
 *
 * NOTE polling:
 *  - fetch immediato al mount
 *  - setTimeout ricorsivo (mai setInterval → evita pile-up se latenza > intervallo)
 *  - cache-buster `_t` sul query param + header `Cache-Control: no-cache`
 *  - pausa se document.hidden; ripartenza automatica al recovery
 *  - cleanup pulito su unmount (nessun setState-after-unmount)
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { lifecycle } from "@/lib/lifecycle";

export function useMessagingPoll(url, {
  intervalMs = 8000,
  params = {},
  enabled = true,
  onData = null,
} = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const mounted = useRef(true);
  const inFlight = useRef(false);
  const timer = useRef(null);
  const paramsRef = useRef(params);
  const onDataRef = useRef(onData);

  // Refs aggiornati ad ogni render → nessuna stale closure.
  paramsRef.current = params;
  onDataRef.current = onData;

  const fetchOnce = useCallback(async () => {
    if (!enabled || inFlight.current) return;
    inFlight.current = true;
    try {
      // Cache-buster + header no-cache → nessun risultato da bfcache/CDN.
      const res = await api.get(url, {
        params: { ...(paramsRef.current || {}), _t: Date.now() },
        headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
      });
      if (!mounted.current) return;
      setData(res.data);
      setError(null);
      if (onDataRef.current) {
        try { onDataRef.current(res.data); } catch { /* silent */ }
      }
    } catch (e) {
      if (!mounted.current) return;
      setError(formatApiError(e));
      // NON interrompiamo il polling: il prossimo tick riproverà.
    } finally {
      inFlight.current = false;
      if (mounted.current) setLoading(false);
    }
  }, [url, enabled]);

  const schedule = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (!enabled || !mounted.current) return;
    if (typeof document !== "undefined" && document.hidden) return;   // pausa in background
    timer.current = setTimeout(async () => {
      await fetchOnce();
      schedule();
    }, intervalMs);
  }, [fetchOnce, intervalMs, enabled]);

  const refresh = useCallback(async () => {
    await fetchOnce();
    schedule();
  }, [fetchOnce, schedule]);

  useEffect(() => {
    mounted.current = true;
    if (!enabled) return;
    // Fetch immediato al mount.
    fetchOnce().then(() => schedule());

    // Cross-device recovery: unificato tramite lifecycle.
    const offRecovery = lifecycle.on("recovery", () => {
      if (mounted.current) { fetchOnce(); schedule(); }
    });

    // Pausa timer quando la tab diventa hidden (risparmio banda/battery).
    // La ripartenza avverrà al prossimo evento "recovery".
    const offHidden = lifecycle.on("hidden", () => {
      if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    });

    return () => {
      mounted.current = false;
      if (timer.current) { clearTimeout(timer.current); timer.current = null; }
      offRecovery();
      offHidden();
    };
  }, [enabled, fetchOnce, schedule]);

  return { data, error, loading, refresh };
}
